import React from 'react';
import PropTypes from 'prop-types';
import { ZCDIcon } from '@zencity/common-ui';
import { SentimentBar } from './SentimentBar';
import ExpandableTable from './ExpandableTable';

const calculatePercentages = (sentimentCounts, subtotal) => {
  const { positive, neutral, negative, mixed, none } = sentimentCounts;
  return {
    positivePercent: parseFloat(((positive / subtotal) * 100).toFixed(2)),
    neutralPercent: parseFloat(((neutral / subtotal) * 100).toFixed(2)),
    negativePercent: parseFloat(((negative / subtotal) * 100).toFixed(2)),
    mixedPercent: parseFloat(((mixed / subtotal) * 100).toFixed(2)),
    nonePercent: parseFloat(((none / subtotal) * 100).toFixed(2)),
  };
};

const columns = [
  { header: 'Boundary', accessorKey: 'label' },
  { header: 'Count', accessorKey: 'count' },
  { header: '%', accessorKey: 'percentage' },
  { header: 'Sentiment', accessorKey: 'sentiment', cell: ({ row }) => row.original.sentiment },
  {
    id: 'expander',
    header: () => {
      return <span>More</span>;
    },
    accessorKey: 'Expend',
    cell: ({ row }) =>
      row.getCanExpand() ? (
        <span
          {...{
            onClick: row.getToggleExpandedHandler(),
            style: {
              paddingLeft: `${row.depth * 2}rem`,
            },
          }}
        >
          {row.getIsExpanded() ? <ZCDIcon name="chevron-down" /> : <ZCDIcon name="chevron-right" />}
        </span>
      ) : null,
  },
];

/**
 * Display the map layer data table with sentiment breakdown
 */
const MapLayerDataTableWithSentimentData = (props) => {
  const { counts, stats, restrictPinsInPolygons } = props;

  const renderSentimentBar = (pinOptionSentimentCount) => {
    const { sentimentCounts, subtotal } = pinOptionSentimentCount;
    const { positive, neutral, negative, mixed } = sentimentCounts;
    const { positivePercent, neutralPercent, negativePercent, mixedPercent } = calculatePercentages(
      sentimentCounts,
      subtotal
    );

    return (
      <SentimentBar
        allowComment
        positive={positive}
        positivePercent={positivePercent}
        mixed={mixed}
        mixedPercent={mixedPercent}
        neutral={neutral}
        neutralPercent={neutralPercent}
        negative={negative}
        negativePercent={negativePercent}
      />
    );
  };

  const tableData = counts.reduce((acc, sentimentCount) => {
    if (sentimentCount.mapLayerId === '0' && restrictPinsInPolygons) return acc;
    acc.push({
      label: sentimentCount.mapLayerId === '0' ? 'Outside Boundaries' : sentimentCount.label,
      count: sentimentCount.subtotal,
      percentage: ((sentimentCount.subtotal / sentimentCount.total) * 100).toFixed(2),
      sentiment: renderSentimentBar(sentimentCount),
      stats: stats[sentimentCount.mapLayerId] || [],
    });

    return acc;
  }, []);

  const renderSubComponent = ({ row }) => {
    const columnArray = [
      { header: '#', accessorKey: 'position' },
      { header: 'Word', accessorKey: 'word' },
      { header: 'Responses', accessorKey: 'ndoc' },
      { header: 'Mentions', accessorKey: 'nentry' },
    ];

    const statsData = row.original.stats.map((stat, index) => ({
      ...stat,
      position: index + 1,
    }));

    return (
      <div>
        <ExpandableTable
          data={statsData}
          columns={columnArray}
          renderSubComponent={() => {}}
          getRowCanExpand={() => false}
        />
      </div>
    );
  };

  if (tableData.length <= 1) return null;

  return (
    <div className="map-layer-data-table-with-sentiment-data">
      <ExpandableTable
        data={tableData}
        columns={columns}
        renderSubComponent={renderSubComponent}
        getRowCanExpand={() => true}
      />
    </div>
  );
};

MapLayerDataTableWithSentimentData.propTypes = {
  counts: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  stats: PropTypes.shape().isRequired,
  restrictPinsInPolygons: PropTypes.bool,
};

MapLayerDataTableWithSentimentData.defaultProps = {
  restrictPinsInPolygons: false,
};

export default MapLayerDataTableWithSentimentData;
